import React from 'react'
import { observable } from 'mobx'
import { observer } from 'mobx-react'
import TagManager from 'react-gtm-module'
import { Button } from 'react-bootstrap'
import { i18nTranslate, isGPSEnabled, convertToBoolean } from 'src/utils'
import { isCrossMarketAllowed } from 'src/utils/marketUtils'
import { isExcludedSavedPaymentType } from 'src/utils/paymentNameUtils'
import isEmpty from 'lodash/isEmpty'
import {
  toastState,
  AddressValidation,
  MetaDecorator,
  tokenExIframe,
  PaymentMarketForm,
  Pagination,
} from 'src/views/components'
import { PaymentCard } from './PaymentCard'
import {
  customerContainer,
  addressContainer,
  accountsAddressContainer,
  tokenExContainer,
  paymetricContainer,
  storeContainer,
  checkoutContainer,
} from 'src/models'
import {
  getNamefromFirstname,
  trackGTMEvents,
  application,
  getTotalPagesForPagination,
  getLiveEventStatus,
  enableBillingAddressRequired,
  isFeatureEnabled,
} from 'src/utils'
import {
  getActivePaymentFormData,
  getNormalizedWalletProfileProperties,
  getRestrictedCardType,
  normalizePaymetricProvider,
} from 'src/utils/paymentContainerHelper'
import './styles.scss'
import { getLocalStorage, APPConfig } from 'config/appConfig'

@observer
class PaymentBlock extends React.Component {
  @observable isCardOpen = false
  @observable show = false
  @observable addressInvalidFlag = false
  @observable recommendations = []
  @observable params = {}
  @observable formDataEnter = {}
  @observable isPaymentFetchInprogress = true
  totalPagesLength = 0
  currentPageDetails = {}

  /**
   * @description Returns the appropriate payment container based on Paymetric flag
   * @returns {Object} tokenExContainer or paymetricContainer
   */
  getPaymentContainer = () => {
    const enableDirectPaymetricAndAccertifyIntegration = convertToBoolean(
      APPConfig.getAppConfig().enableDirectPaymetricAndAccertifyIntegration
    )
    return enableDirectPaymetricAndAccertifyIntegration
      ? paymetricContainer
      : tokenExContainer
  }

  getProfilePaymentProperties = () => {
    return getNormalizedWalletProfileProperties({
      paymentContainer: this.getPaymentContainer(),
      paymetricContainer,
    })
  }

  constructor(props) {
    super(props)
  }

  async componentDidMount() {
    this.isPaymentFetchInprogress = true
    const restrictCreditCardTypesFromGPS = isFeatureEnabled(
      'restrictCreditCardTypesFromGPS'
    )
    const shouldFetchGlobalPayment =
      isGPSEnabled() &&
      restrictCreditCardTypesFromGPS &&
      (!checkoutContainer.globalPaymentResponse ||
        Object.keys(checkoutContainer.globalPaymentResponse).length === 0)

    await Promise.all([
      this.getPayments(),
      shouldFetchGlobalPayment
        ? checkoutContainer.getGlobalPaymentOptions(false, true)
        : Promise.resolve(),
    ])
    this.isPaymentFetchInprogress = false
  }

  getPayments = async () => {
    await Promise.all([
      customerContainer.getPayment(),
      addressContainer.getProfileAddress(),
    ])
    addressContainer.addressData.length === 0 &&
      addressContainer.getProfileAddress()

    trackGTMEvents({
      event: 'nuskin-profile',
      eventType: 'Profile-Payment',
    })
  }

  renderList = (item, index) => {
    return (
      !isExcludedSavedPaymentType(item.type) && (
        <PaymentCard
          cardData={item}
          key={index}
          index={index}
          pageDetails={this.currentPageDetails}
        />
      )
    )
  }

  toggleCardState = () => {
    tokenExIframe.isValidCard = true
    tokenExIframe.isCvvValidCard = true
    this.isCardOpen = !this.isCardOpen
    if (this.isCardOpen) {
      checkoutContainer.addPaymentToProfile = false
    }
  }

  findDefaultAddress = () => {
    const accountaddress =
      accountsAddressContainer.addressData?.length > 0
        ? accountsAddressContainer.addressData
        : []
    const addressData =
      addressContainer.addressData?.length > 0
        ? addressContainer.addressData
        : accountaddress
    let result = addressData.find(address => {
      if (address.isDefault === true) {
        return true
      }
    })
    return result ? result : {}
  }

  onAddingPaymentCard = async cardDetail => {
    const {
      cardFirstName,
      cardNumber,
      expiryDate,
      setToDefaultPayment,
      billingSameAsShipping,
      isToMakeValidationApiCall = false,
      ...addressParams
    } = cardDetail

    const addressData =
      addressContainer.addressData?.length > 0
        ? addressContainer.addressData
        : accountsAddressContainer.addressData
    if (billingSameAsShipping === true && isEmpty(addressData)) {
      toastState.setToastMessage(
        i18nTranslate(
          'address.defaultEmpty',
          'You do not have a default shipping address. '
        ),
        false
      )
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0)
      }
      return
    }
    let billingAddress =
      billingSameAsShipping === true ? this.findDefaultAddress() : addressParams
    billingAddress.email =
      addressParams.email || customerContainer?.profileResponse?.email
    billingAddress.addressLine2 = billingAddress?.addressLine2 || ''

    if (billingAddress.lastName === null) {
      billingAddress.firstName = customerContainer.profileResponse.firstName
      billingAddress.lastName = customerContainer.profileResponse.lastName
    }
    const activeFormData = getActivePaymentFormData({
      tokenExContainer,
      paymetricContainer,
    })
    const cardProviderRaw = paymetricContainer.getCardProviderFromTokenData(
      activeFormData || {}
    )
    const cardProvider = normalizePaymetricProvider(cardProviderRaw)
    const restrictedCardType = getRestrictedCardType(cardProvider)
    if (restrictedCardType) {
      toastState.setToastMessage(
        i18nTranslate(restrictedCardType.key, restrictedCardType.fallback),
        false
      )
      return
    }
    const name = getNamefromFirstname(cardFirstName)
    const params = {
      type: 'CREDITCARD',
      isDefault: setToDefaultPayment.toString(),
      // name: cardFirstName,
      // NUSKIN-893 - Unable to save payment if full name is provided
      billingAddress,
      accountInfo: {
        name: name.firstName,
        lastName: name.lastName,
        cardNumber,
        expiryMonth: expiryDate?.split('/')[0],
        expiryYear: '20' + expiryDate?.split('/')[1],
        isDefault: setToDefaultPayment.toString(),
        provider: cardProvider,
      },
      option: {
        addPaymentToProfile: true,
      },
      properties: {
        ...this.getProfilePaymentProperties(),
      },
    }
    const isBillingAddressRequired = enableBillingAddressRequired()
    if (!isBillingAddressRequired) {
      params.billingAddress = null
    }
    delete cardDetail.expiryDate
    delete cardDetail.undefined
    if (!cardDetail.billingSameAsShipping) {
      delete cardDetail.cardFirstName
      delete cardDetail.cardLastName
      delete cardDetail.cardNumber
      delete cardDetail.expiryMonth
      delete cardDetail.expiryYear
      delete cardDetail.cardCVV
      delete cardDetail.billingSameAsShipping
      delete cardDetail.addPaymentToProfile
      delete cardDetail.paymentMethodType
      const cardValue = {
        ...cardDetail,
        // country: storeContainer.storeIDValue,
      }

      if (
        checkoutContainer.selectedMarket === 'US' ||
        checkoutContainer.selectedMarket === 'CA' ||
        isToMakeValidationApiCall
      ) {
        const res = await addressContainer.validateAddress(cardValue)

        if (res?.code === 'ADCOVAL0003') {
          // this.show = true
          this.addressInvalidFlag = true
          this.recommendations = [{ ...cardValue }, []]
          this.formDataEnter = { ...cardValue }
          this.params = { ...params }
          this.show = true
        } else if (res?.code == 'ONESOURCE_ADPLGIN_VAL_ERROR') {
          this.setState({
            show: false,
          })
          if (typeof window !== 'undefined') {
            window.scrollTo(0, 0)
          }
          toastState.setToastMessage(
            i18nTranslate('address.NotAdded', 'Address not added')
          )
          return
        } else if (res?.code == 'AOACHLD0001') {
          this.setState({
            show: false,
          })
          if (typeof window !== 'undefined') {
            window.scrollTo(0, 0)
          }
          toastState.setToastMessage(
            i18nTranslate(
              'myaccount.userHoldStatus',
              'There is an issue with your account. Please contact you local account manager.'
            )
          )
          return
        } else {
          this.addressInvalidFlag = false
          const recommendationAddress =
            res?.address?.validationDetails?.[0]?.recommendations || []
          let firstAddress = recommendationAddress?.[0] || {}
          // this.show = true
          if (Object.keys(firstAddress)?.length > 0) {
            this.recommendations = [{ ...cardValue }, ...recommendationAddress]
            this.params = { ...params }
            this.formDataEnter = { ...cardValue }
            this.show = true
          } else {
            this.formDataEnter = { ...cardValue }
            this.params = { ...params }
            this.handleClickOnPro(cardDetail)
          }
        }
      } else {
        this.addressInvalidFlag = true
        this.recommendations = [{ ...cardValue }, []]
        this.formDataEnter = { ...cardValue }
        this.params = { ...params }
        this.handleClickOnPro(cardDetail)
      }
    } else {
      await customerContainer.addPayment(params)
      if (customerContainer.isPaymentCardState === false) {
        this.toggleCardState()
      }
    }
  }

  async handleClickOnPro(formData) {
    const { accountInfo } = this.params
    const reqParams = {
      // name: formData.firstName,
      // NUSKIN-893 - Unable to save payment if full name is provided
      type: 'CREDITCARD',
      accountInfo,
      billingAddress: {
        ...formData,
        email: this.params.billingAddress?.email,
      },
      properties: {
        ...this.getProfilePaymentProperties(),
      },
      isDefault: this.params.isDefault,
    }
    if (formData?.selectedIndex <= 0) {
      await customerContainer?.validateOriginalAddress(reqParams)
    } else {
      await customerContainer?.addPayment(reqParams)
    }
    this.toggleCardState()
    this.handleClose()
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0)
    }
  }
  handleClose = () => {
    this.show = false
  }

  handlePagination = (activePage = 1) => {
    customerContainer.getPayment(activePage)
    if (typeof window === 'object') {
      window.scrollTo(0, 0)
    }
  }

  selectPaymentMethodPropertiesForGTM = paymentMethodKeys => {
    const { id, name, isDefault, status, userId, type, valueType } =
      paymentMethodKeys
    return { id, name, isDefault, status, userId, type, valueType }
  }

  /**
   * @description
   * Description placeholder
   * @date 11/27/2023 - 12:44:43 PM
   * Pagination component props
   * totalPages( as totalCount ) to be received from get all payments api
   * active( as page) to be received from get all payments api
   * @returns {*}
   */
  render() {
    const { pageableInfo = {}, payments = [] } =
      customerContainer?.paymentResponse || {}
    const { page = 1, count = 1 } = pageableInfo || {}

    this.currentPageDetails = {
      page: count == 1 ? page - 1 : page,
    }
    this.totalPagesLength = getTotalPagesForPagination(pageableInfo)
    const isPaymentPaginationEnabled =
      APPConfig?.getAppConfig()?.isPaymentPaginationEnabled === 'true' || false

    const paymentCardView = payments?.length
      ? payments?.map(this.renderList)
      : ''
    const myAccountPaymentTitle = i18nTranslate(
      'myAccountPaymentPage.title',
      'Payments | Nu Skin'
    )
    if (payments?.length > 0) {
      const paymentMethodDetailsForGTM = payments?.map(
        this.selectPaymentMethodPropertiesForGTM
      )
      const enableLiveEvent = getLiveEventStatus()
      TagManager.dataLayer({
        dataLayer: {
          event: 'NuskinProfilePaymentMethods',
          pagePath: '/myaccount/payments',
          pageTitle: 'Payments',
          paymentMethods: paymentMethodDetailsForGTM,
          platform: 'equinox',
        },
      })

      if (enableLiveEvent === 'true') {
        TagManager.dataLayer({
          dataLayer: {
            event: 'view_payment_methods',
          },
        })
      }
    }

    const isCrossMarketAllowedFlag =
      customerContainer.isCrossMarketAllowed() || false

    return (
      <>
        <MetaDecorator
          title={myAccountPaymentTitle}
          imageUrl=""
          imageAlt=""
          description="The Payments Page"
          location={this.props}
        />
        <section
          className={
            isCrossMarketAllowedFlag ? `payment-block-wrapper` : 'd-none'
          }>
          <div className="add-payment-header p-3 shadow-sm mb-3">
            <h1
              className=" d-flex align-items-center mb-0 payment-header heading-2"
              data-testid="qa-payment-heading">
              {i18nTranslate('payment.paymentMethodLabel', 'Payment Methods')}
            </h1>
            {this.show && (
              <AddressValidation
                open={this.show}
                addressInvalidFlag={this.addressInvalidFlag}
                recommendations={this.recommendations}
                handleClose={() => this.handleClose()}
                handleClickOnPro={dataAfterValidation =>
                  this.handleClickOnPro(dataAfterValidation)
                }
              />
            )}
            <Button
              style={{
                pointerEvents:
                  this.isCardOpen || this.isPaymentFetchInprogress
                    ? 'none'
                    : '',
              }}
              variant={this.isCardOpen ? 'light' : 'primary'}
              className="payment-btn px-4"
              onClick={this.toggleCardState}
              disabled={this.isPaymentFetchInprogress}
              data-testid="qa-add-payment-button">
              {i18nTranslate('payment.addNewCard', 'Add New Card')}
            </Button>
          </div>
          {this.isCardOpen && (
            <div className="add-new-payment bg-white p-3">
              <PaymentMarketForm
                isFromPayment={true}
                includePaymetricCvv={false}
                onCancel={this.toggleCardState}
                onSubmitForm={this.onAddingPaymentCard}
                billingAddressType="paymentsMethod"
              />
            </div>
          )}
          {paymentCardView}
        </section>

        {this.totalPagesLength > 1 && isPaymentPaginationEnabled && (
          <Pagination
            handlePagination={this.handlePagination}
            totalPages={this.totalPagesLength}
            active={page}
            numOfPaginationsToShow={this.totalPagesLength}
            alignPagination={application.isMobile ? 'center' : 'start'}
          />
        )}
        <div
          className={
            isCrossMarketAllowedFlag ? 'd-none' : 'non-home-market-country'
          }>
          {i18nTranslate(
            'myprofile.submit.message',
            'Please switch to your home market to make any changes.'
          )}
        </div>
      </>
    )
  }
}

export { PaymentBlock }
export default PaymentBlock
